import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalHeaderContent,
  ModalIconWrapper,
  ModalTitle,
  ModalSubtitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalFormField,
  ModalLabel,
  ModalInputWrapper,
  ModalInput,
  ModalSelect,
  ModalTextarea,
  ModalErrorText,
  ModalCancelButton,
  ModalSubmitButton,
  Spinner,
} from "./ModalStyles";
import { FiX, FiFolder, FiFileText, FiMapPin, FiHome, FiBriefcase, FiUser, FiCreditCard, FiEdit3, FiHash, FiArrowRight } from "react-icons/fi";
import { DueDiligenceTier, DueDiligenceOperationType } from "../../../Common/Types-DTOs/DueDiligence";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { useApi } from "../../../Common/Hooks/useApi";
import { useBuyCredits } from "../../../Common/Hooks/useBuyCredits";
import { DueDiligencePlanOffer } from "../../../Common/Hooks/useApi/Schemas";
import { isErr } from "../../../Common/Utils/Result";
import { DueDiligenceOneOffPrices } from "../../../Components/DueDiligencePlanPicker";
import { TermsConsent } from "../../../Components/TermsConsent";
import {
  DueDiligenceCreditCosts,
  NewCasePayment,
  PaymentSection,
  creditsPayTier,
  paysWithCredits,
  resolveNewCasePayment,
} from "./PaymentSection";


// The modal says what happens to the case once created, by how it gets paid
const SUBTITLE_KEYS: Record<NewCasePayment["kind"], string> = {
  credits: "dueDiligence.newCaseModal.payment.subtitle.credits",
  plan: "dueDiligence.newCaseModal.payment.subtitle.plan",
  oneOff: "dueDiligence.newCaseModal.header.subtitle",
};
const SUBMIT_KEYS: Record<NewCasePayment["kind"], string> = {
  credits: "dueDiligence.newCaseModal.payment.submit.credits",
  plan: "dueDiligence.newCaseModal.payment.submit.plan",
  oneOff: "dueDiligence.newCaseModal.submitButton",
};
const SUBMITTING_KEYS: Record<NewCasePayment["kind"], string> = {
  credits: "dueDiligence.newCaseModal.payment.submitting.credits",
  plan: "dueDiligence.newCaseModal.payment.submitting.plan",
  oneOff: "dueDiligence.newCaseModal.submittingButton",
};

const OPERATION_TYPE_VALUES: DueDiligenceOperationType[] = [
  "acquisition_investment",
  "divestment_sale",
  "land_development",
  "other",
];

const OPERATION_TYPE_OPTION_KEYS: Record<DueDiligenceOperationType, string> = {
  acquisition_investment: "dueDiligence.newCaseModal.operationTypeOptions.acquisitionInvestment",
  divestment_sale: "dueDiligence.newCaseModal.operationTypeOptions.divestmentSale",
  land_development: "dueDiligence.newCaseModal.operationTypeOptions.landDevelopment",
  other: "dueDiligence.newCaseModal.operationTypeOptions.other",
};

interface NewCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: {
    name: string;
    propertyAddress: string;
    propertyType: string;
    cadastralReference: string;
    tier: DueDiligenceTier;
    operationType: DueDiligenceOperationType | "";
    observations: string;
    ownerName: string;
    ownerIdCard: string;
    acceptedTerms: boolean;
  };
  errors: Record<string, string>;
  isLoading: boolean;
  submitAttempts: number;
  onInputChange: (field: string, value: string | boolean) => void;
  /** Creates the case and pays it as chosen in the modal. */
  onSubmit: (payment: NewCasePayment) => void;
}

export const NewCaseModal: React.FC<NewCaseModalProps> = ({
  isOpen,
  onClose,
  formData,
  errors,
  isLoading,
  submitAttempts,
  onInputChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { buyCredits } = useBuyCredits();
  const { business } = useBusiness();
  const { getDueDiligencePricing } = useDueDiligence();
  const { getDueDiligencePlansEndpoint } = useApi();
  const [pricing, setPricing] = useState<DueDiligenceOneOffPrices | null>(null);
  const [creditCosts, setCreditCosts] = useState<DueDiligenceCreditCosts | null>(null);
  const [plans, setPlans] = useState<DueDiligencePlanOffer[] | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const credits = business?.credits;
  const payment = resolveNewCasePayment(credits, selectedPlan);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlan(null);
      return;
    }
    getDueDiligencePricing().then((result) => {
      if (isErr(result)) {
        Sentry.captureMessage("DD pricing fetch failed", { level: "error", extra: { result } });
        return;
      }
      setPricing(result.value.pricing);
      setCreditCosts(result.value.creditCosts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Plans are only offered to whoever isn't subscribed
  useEffect(() => {
    if (!isOpen || paysWithCredits(credits) || plans) return;
    getDueDiligencePlansEndpoint().then((result) => {
      if (!isErr(result)) setPlans(result.value.plans);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, credits]);

  // Paying with credits, a tier they can't pay blocks the button; the case
  // details still fall back to the card if the payment fails anyway
  const creditsFallShort = payment.kind === "credits" && !(credits && creditsPayTier(credits, creditCosts, formData.tier));
  const nameRef = useRef<HTMLInputElement>(null);
  const propertyAddressRef = useRef<HTMLInputElement>(null);
  const cadastralReferenceRef = useRef<HTMLInputElement>(null);
  const operationTypeRef = useRef<HTMLSelectElement>(null);
  const acceptedTermsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Takes the user to the first required field left empty, in the order they show
  useEffect(() => {
    if (submitAttempts === 0) return;
    const fieldRefs: [string, React.RefObject<HTMLInputElement | HTMLSelectElement>][] = [
      ["name", nameRef],
      ["propertyAddress", propertyAddressRef],
      ["operationType", operationTypeRef],
      ["cadastralReference", cadastralReferenceRef],
      ["acceptedTerms", acceptedTermsRef],
    ];
    const firstMissing = fieldRefs.find(([field]) => errors[field])?.[1].current;
    firstMissing?.scrollIntoView({ behavior: "smooth", block: "center" });
    firstMissing?.focus({ preventScroll: true });
  }, [submitAttempts]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = (
    <ModalOverlay onClick={handleBackdropClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalHeaderContent>
            <ModalIconWrapper>
              <FiFolder size={24} />
            </ModalIconWrapper>
            <div>
              <ModalTitle>{t("dueDiligence.newCaseModal.header.title")}</ModalTitle>
              <ModalSubtitle>
                {t(SUBTITLE_KEYS[payment.kind])}
              </ModalSubtitle>
            </div>
          </ModalHeaderContent>
          <ModalCloseButton onClick={onClose}>
            <FiX size={20} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <div>
            <ModalFormField>
              <ModalLabel>
                {t("dueDiligence.newCaseModal.fields.name.label")} <span style={{ color: "#D4443A" }}>*</span>
              </ModalLabel>
              <ModalInputWrapper>
                <FiFileText className="input-icon" />
                <ModalInput
                  ref={nameRef}
                  type="text"
                  placeholder={t("dueDiligence.newCaseModal.fields.name.placeholder")}
                  value={formData.name}
                  onChange={(e) => onInputChange("name", e.target.value)}
                  $hasError={!!errors.name}
                />
              </ModalInputWrapper>
              {errors.name && <ModalErrorText>{errors.name}</ModalErrorText>}
            </ModalFormField>

            <ModalFormField>
              <ModalLabel>
                {t("dueDiligence.newCaseModal.fields.propertyAddress.label")} <span style={{ color: "#D4443A" }}>*</span>
              </ModalLabel>
              <ModalInputWrapper>
                <FiMapPin className="input-icon" />
                <ModalInput
                  ref={propertyAddressRef}
                  type="text"
                  placeholder={t("dueDiligence.newCaseModal.fields.propertyAddress.placeholder")}
                  value={formData.propertyAddress}
                  onChange={(e) => onInputChange("propertyAddress", e.target.value)}
                  $hasError={!!errors.propertyAddress}
                />
              </ModalInputWrapper>
              {errors.propertyAddress && <ModalErrorText>{errors.propertyAddress}</ModalErrorText>}
            </ModalFormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
              <ModalFormField>
                <ModalLabel>{t("dueDiligence.newCaseModal.fields.propertyType.label")}</ModalLabel>
                <ModalInputWrapper>
                  <FiHome className="input-icon" />
                  <ModalInput
                    type="text"
                    placeholder={t("dueDiligence.newCaseModal.fields.propertyType.placeholder")}
                    value={formData.propertyType}
                    onChange={(e) => onInputChange("propertyType", e.target.value)}
                    $hasError={!!errors.propertyType}
                  />
                </ModalInputWrapper>
                {errors.propertyType && <ModalErrorText>{errors.propertyType}</ModalErrorText>}
              </ModalFormField>

              <ModalFormField>
                <ModalLabel>
                  {t("dueDiligence.newCaseModal.fields.operationType.label")} <span style={{ color: "#D4443A" }}>*</span>
                </ModalLabel>
                <ModalInputWrapper>
                  <FiBriefcase className="input-icon" />
                  <ModalSelect
                    ref={operationTypeRef}
                    value={formData.operationType}
                    onChange={(e) => onInputChange("operationType", e.target.value)}
                    $hasError={!!errors.operationType}
                  >
                    <option value="">{t("dueDiligence.newCaseModal.fields.operationType.placeholder")}</option>
                    {OPERATION_TYPE_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {t(OPERATION_TYPE_OPTION_KEYS[value])}
                      </option>
                    ))}
                  </ModalSelect>
                </ModalInputWrapper>
                {errors.operationType && <ModalErrorText>{errors.operationType}</ModalErrorText>}
              </ModalFormField>
            </div>

            <ModalFormField>
              <ModalLabel>
                {t("dueDiligence.newCaseModal.fields.cadastralReference.label")} <span style={{ color: "#D4443A" }}>*</span>
              </ModalLabel>
              <ModalInputWrapper>
                <FiHash className="input-icon" />
                <ModalInput
                  ref={cadastralReferenceRef}
                  type="text"
                  maxLength={20}
                  placeholder={t("dueDiligence.newCaseModal.fields.cadastralReference.placeholder")}
                  value={formData.cadastralReference}
                  onChange={(e) => onInputChange("cadastralReference", e.target.value)}
                  $hasError={!!errors.cadastralReference}
                />
              </ModalInputWrapper>
              {errors.cadastralReference && <ModalErrorText>{errors.cadastralReference}</ModalErrorText>}
            </ModalFormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
              <ModalFormField>
                <ModalLabel>{t("dueDiligence.newCaseModal.fields.ownerName.label")}</ModalLabel>
                <ModalInputWrapper>
                  <FiUser className="input-icon" />
                  <ModalInput
                    type="text"
                    placeholder={t("dueDiligence.newCaseModal.fields.ownerName.placeholder")}
                    value={formData.ownerName}
                    onChange={(e) => onInputChange("ownerName", e.target.value)}
                  />
                </ModalInputWrapper>
              </ModalFormField>

              <ModalFormField>
                <ModalLabel>{t("dueDiligence.newCaseModal.fields.ownerIdCard.label")}</ModalLabel>
                <ModalInputWrapper>
                  <FiCreditCard className="input-icon" />
                  <ModalInput
                    type="text"
                    placeholder={t("dueDiligence.newCaseModal.fields.ownerIdCard.placeholder")}
                    value={formData.ownerIdCard}
                    onChange={(e) => onInputChange("ownerIdCard", e.target.value)}
                  />
                </ModalInputWrapper>
              </ModalFormField>
            </div>

            <ModalFormField>
              <ModalLabel>{t("dueDiligence.newCaseModal.fields.observations.label")}</ModalLabel>
              <ModalInputWrapper style={{ alignItems: "flex-start" }}>
                <FiEdit3 className="input-icon" style={{ top: "1.1rem", transform: "none" }} />
                <ModalTextarea
                  placeholder={t("dueDiligence.newCaseModal.fields.observations.placeholder")}
                  value={formData.observations}
                  onChange={(e) => onInputChange("observations", e.target.value)}
                  style={{ paddingLeft: "2.75rem" }}
                />
              </ModalInputWrapper>
            </ModalFormField>

            <ModalFormField>
              <ModalLabel>
                {t("dueDiligence.newCaseModal.fields.tier.label")} <span style={{ color: "#D4443A" }}>*</span>
              </ModalLabel>
              <div style={{ marginTop: "0.5rem" }}>
                <PaymentSection
                  tier={formData.tier}
                  onTierChange={(tier) => onInputChange("tier", tier)}
                  pricing={pricing}
                  creditCosts={creditCosts}
                  credits={credits}
                  plans={plans}
                  payment={payment}
                  onChangePlan={() => {
                    onClose();
                    navigate("/due-diligence-plans");
                  }}
                  onBuyCredits={buyCredits}
                  onPlanChange={setSelectedPlan}
                  selectedPlan={selectedPlan}
                />
              </div>
            </ModalFormField>

            <TermsConsent
              checked={formData.acceptedTerms}
              onChange={(checked) => onInputChange("acceptedTerms", checked)}
              disabled={isLoading}
              inputRef={acceptedTermsRef}
            />
            {errors.acceptedTerms && <ModalErrorText>{errors.acceptedTerms}</ModalErrorText>}
          </div>
        </ModalBody>

        <ModalFooter>
          <div style={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
            <span style={{ color: "#D4443A" }}>*</span> {t("dueDiligence.newCaseModal.requiredFieldsNote")}
          </div>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <ModalCancelButton onClick={onClose} disabled={isLoading}>
              {t("dueDiligence.newCaseModal.cancelButton")}
            </ModalCancelButton>
            <ModalSubmitButton onClick={() => onSubmit(payment)} disabled={isLoading || creditsFallShort}>
              {isLoading ? t(SUBMITTING_KEYS[payment.kind]) : t(SUBMIT_KEYS[payment.kind])}
              {isLoading ? <Spinner /> : <FiArrowRight size={16} />}
            </ModalSubmitButton>
          </div>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
};
